import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import gsap from 'gsap'

import { cn } from '@/lib/utils'
import { easeLoad } from '@/lib/motion'
import { event, navLinks } from '@/constants/metric'
import { ActionButton } from '@/components/generals/action-button'
import { SwapLabel } from '@/components/generals/swap-label'

export const Navbar: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    useEffect(() => {
        const panel = panelRef.current

        if (!open || !panel) return

        const ctx = gsap.context(() => {
            gsap.fromTo('[data-menu-panel]', { yPercent: -100 }, {
                yPercent: 0,
                duration: 0.7,
                ease: 'power4.out'
            })

            gsap.fromTo('[data-menu-item]', { y: 34, opacity: 0 }, {
                y: 0,
                opacity: 1,
                duration: 0.9,
                delay: 0.16,
                stagger: 0.06,
                ease: easeLoad
            })
        }, panel)

        return () => ctx.revert()
    }, [open])

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router])

    return (
        <>
            <div
                data-load='down'
                data-load-delay='0.2'
                className='fixed inset-x-0 top-0 z-[90]'
            >
                <div className='mx-auto w-full max-w-[1600px] border-b border-line bg-[#0d0d0d] px-0 tab:px-[60px]'>
                    <nav
                        aria-label='Main'
                        className='fw-container relative flex h-[64px] items-center justify-between gap-6 tab:h-[80px]'
                    >
                        <Link
                            href='/'
                            className='fw-swap-host shrink-0 font-sans text-[13px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink tab:text-[14px]'
                        >
                            <SwapLabel>
                                {event.wordmark}
                            </SwapLabel>
                        </Link>

                        <ul className='absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 desk:flex'>
                            {navLinks.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        className='fw-swap-host block font-sans text-[14px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink/90 transition-colors duration-300 hover:text-ink'
                                    >
                                        <SwapLabel>
                                            {link.label}
                                        </SwapLabel>
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <div className='flex items-center gap-3'>
                            <ActionButton
                                href={event.ticketsUrl}
                                variant='outline'
                                size='sm'
                                className='hidden tab:inline-flex'
                            >
                                Get tickets
                            </ActionButton>

                            <button
                                type='button'
                                aria-expanded={open}
                                aria-controls='mobile-menu'
                                aria-label='Open menu'
                                onClick={() => setOpen(true)}
                                className='flex h-[38px] w-[38px] items-center justify-center border border-[#454545] text-ink transition-colors duration-300 hover:border-[#fefff5] desk:hidden'
                            >
                                <span className='sr-only'>
                                    Open menu
                                </span>

                                <span aria-hidden='true' className='flex h-[10px] w-[16px] flex-col justify-between'>
                                    <span className='block h-px w-full bg-current' />
                                    <span className='block h-px w-full bg-current' />
                                </span>
                            </button>
                        </div>
                    </nav>
                </div>
            </div>

            {open ? (
                <div
                    ref={panelRef}
                    id='mobile-menu'
                    className='fixed inset-0 z-[110] desk:hidden'
                >
                    <button
                        type='button'
                        aria-label='Close menu'
                        onClick={() => setOpen(false)}
                        className='absolute inset-0 h-full w-full cursor-default bg-black/60'
                    />

                    <div
                        data-menu-panel
                        className='relative flex h-full w-full flex-col justify-between border-b border-line bg-[#0d0d0d] px-5 pb-10 pt-0 tab:px-[60px]'
                    >
                        <div className='flex h-[64px] shrink-0 items-center justify-between border-b border-line tab:h-[80px]'>
                            <span className='font-sans text-[13px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink tab:text-[14px]'>
                                {event.wordmark}
                            </span>

                            <button
                                type='button'
                                aria-label='Close menu'
                                onClick={() => setOpen(false)}
                                className='flex h-[38px] w-[38px] items-center justify-center border border-[#454545] text-ink transition-colors duration-300 hover:border-[#fefff5]'
                            >
                                <span aria-hidden='true' className='relative block h-[14px] w-[14px]'>
                                    <span className='absolute left-0 top-1/2 block h-px w-full rotate-45 bg-current' />
                                    <span className='absolute left-0 top-1/2 block h-px w-full -rotate-45 bg-current' />
                                </span>
                            </button>
                        </div>

                        <ul className='flex flex-1 flex-col justify-center gap-2 py-10'>
                            {navLinks.map((link) => (
                                <li key={link.href} data-menu-item>
                                    <Link
                                        href={link.href}
                                        onClick={() => setOpen(false)}
                                        className={cn(
                                            'block py-2 font-sans text-[36px] font-normal leading-[1.05em] tracking-[-0.02em] text-ink',
                                            'transition-colors duration-300 hover:text-white tab:text-[50px]'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <div data-menu-item className='shrink-0'>
                            <ActionButton
                                href={event.ticketsUrl}
                                variant='solid'
                                block
                                onClick={() => setOpen(false)}
                            >
                                Get tickets
                            </ActionButton>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    )
}
